import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, Trash2, X } from "lucide-react";
import { useCart } from "@/context/CartContext";

const FREE_SHIPPING_AT = 150;

export const CartDrawer = () => {
  const { items, cartOpen, setCartOpen, updateQty, removeItem, subtotal, setCheckoutOpen } = useCart();
  const progress = Math.min(subtotal / FREE_SHIPPING_AT, 1);

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed right-0 top-0 z-50 h-full w-full max-w-md bg-[#FFFFFF] border-l border-[#E4E4DE] flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-between border-b border-[#E4E4DE] px-6 py-5">
              <h2 data-testid="cart-title" className="font-display text-xl font-bold uppercase tracking-tight">
                Your cart <span className="text-[#4D7C0F]">({items.reduce((n, i) => n + i.qty, 0)})</span>
              </h2>
              <button
                data-testid="cart-close-btn"
                onClick={() => setCartOpen(false)}
                className="h-10 w-10 flex items-center justify-center border border-[#E4E4DE] text-zinc-500 hover:text-[#4D7C0F] hover:border-[#C8FF1E] transition-colors"
                aria-label="Close cart"
              >
                <X size={18} />
              </button>
            </div>

            <div className="border-b border-[#E4E4DE] px-6 py-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                {subtotal >= FREE_SHIPPING_AT
                  ? "Free shipping unlocked"
                  : `$${(FREE_SHIPPING_AT - subtotal).toFixed(0)} away from free shipping`}
              </p>
              <div className="mt-2 h-1 bg-[#E4E4DE]">
                <div
                  data-testid="shipping-progress"
                  className="h-full bg-[#C8FF1E] transition-all duration-500"
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <p data-testid="cart-empty-message" className="py-16 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">
                  Your cart is empty
                </p>
              ) : (
                <ul className="divide-y divide-[#E4E4DE]">
                  {items.map((item) => (
                    <li key={item.key} data-testid={`cart-item-${item.key}`} className="flex gap-4 py-4">
                      <img src={item.image} alt={item.name} className="h-20 w-16 object-cover border border-[#E4E4DE]" />
                      <div className="flex-1">
                        <div className="flex justify-between gap-2">
                          <p className="font-display text-sm font-bold uppercase tracking-tight">{item.name}</p>
                          <button
                            data-testid={`cart-remove-${item.key}`}
                            onClick={() => removeItem(item.key)}
                            className="text-zinc-500 hover:text-red-500 transition-colors"
                            aria-label="Remove item"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                          Size {item.size}
                        </p>
                        <div className="mt-2 flex items-center justify-between">
                          <div className="flex items-center border border-[#E4E4DE]">
                            <button
                              data-testid={`cart-qty-minus-${item.key}`}
                              onClick={() => updateQty(item.key, -1)}
                              className="h-8 w-8 flex items-center justify-center text-zinc-600 hover:text-[#4D7C0F]"
                              aria-label="Decrease quantity"
                            >
                              <Minus size={13} />
                            </button>
                            <span data-testid={`cart-qty-${item.key}`} className="w-8 text-center font-mono text-xs">{item.qty}</span>
                            <button
                              data-testid={`cart-qty-plus-${item.key}`}
                              onClick={() => updateQty(item.key, 1)}
                              className="h-8 w-8 flex items-center justify-center text-zinc-600 hover:text-[#4D7C0F]"
                              aria-label="Increase quantity"
                            >
                              <Plus size={13} />
                            </button>
                          </div>
                          <p className="font-mono text-sm font-bold text-[#4D7C0F]">
                            ${(item.price * item.qty).toFixed(0)}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-[#E4E4DE] px-6 py-5">
                <div className="flex justify-between font-mono text-sm">
                  <span className="uppercase tracking-widest text-zinc-500">Subtotal</span>
                  <span data-testid="cart-subtotal" className="font-bold text-zinc-900">${subtotal.toFixed(2)}</span>
                </div>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-600">
                  Shipping calculated at checkout
                </p>
                <button
                  data-testid="cart-checkout-btn"
                  onClick={() => {
                    setCartOpen(false);
                    setCheckoutOpen(true);
                  }}
                  className="mt-4 w-full bg-[#C8FF1E] py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black hover:bg-[#B0E600] transition-colors"
                >
                  Proceed to secure checkout
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
